// Illustration de l'accueil d'un projet neuf : « de la mesure à l'effet prouvé ».
// Cinq étapes reliées par un chemin en pointillés : décrire le produit (la loupe),
// mesurer (la liste cochée, les barres), comprendre pourquoi (les utilisateurs),
// décider (l'ampoule), prouver l'effet (la jauge et sa courbe qui monte). Le
// parcours du produit, dessiné.
//
// Même langage que `ResearchIllustration` (portfolio/Illustration.tsx) : au
// trait, teintes de catégorie (`--hue-*`), sans ombre ni dégradé. Elle suit donc
// le thème clair ou sombre seule. Purement décorative — `aria-hidden`.
import '../portfolio/portfolio.css';

function Num({ x, y, n }: { x: number; y: number; n: number }) {
  return (
    <g>
      <circle className="i-num-bg" cx={x} cy={y} r="10" />
      <text className="i-num" x={x} y={y + 4} textAnchor="middle">{n}</text>
    </g>
  );
}

export function JourneyIllustration() {
  return (
    <svg className="illu illu-journey" viewBox="0 0 440 430" fill="none" aria-hidden="true">
      {/* — Le chemin — */}
      <path className="i-flow" d="M176 62C236 62 236 104 262 112" />
      <path className="i-flow" d="M300 166C300 196 250 196 214 208" />
      <path className="i-flow" d="M214 252C244 262 248 272 262 280" />
      <path className="i-flow" d="M300 334C300 352 280 356 262 362" />

      {/* 1 — Décrire le produit : la loupe */}
      <rect className="i-card" x="22" y="22" width="154" height="80" rx="14" />
      <rect className="i-tint hue-cyan" x="38" y="38" width="48" height="48" rx="12" />
      <circle className="i-stroke hue-cyan" cx="59" cy="59" r="10" />
      <path className="i-stroke hue-cyan" d="M66.5 66.5l8 8" />
      <path className="i-line" d="M100 52h56" />
      <path className="i-line" d="M100 66h40" />
      <Num x={22} y={22} n={1} />

      {/* 2 — Mesurer : la liste cochée, les barres */}
      <rect className="i-card" x="262" y="86" width="158" height="80" rx="14" />
      <path className="i-stroke hue-violet" d="M278 108l4 4 8-8" />
      <path className="i-line" d="M298 108h34" />
      <path className="i-stroke hue-violet" d="M278 126l4 4 8-8" />
      <path className="i-line" d="M298 126h26" />
      <rect className="i-tint hue-violet" x="278" y="138" width="12" height="12" rx="3" />
      <path className="i-line" d="M298 144h30" />
      <rect className="i-fill hue-blue" x="346" y="126" width="14" height="24" rx="3.5" />
      <rect className="i-fill hue-violet" x="366" y="114" width="14" height="36" rx="3.5" />
      <rect className="i-fill hue-cyan" x="386" y="102" width="14" height="48" rx="3.5" />
      <Num x={262} y={86} n={2} />

      {/* 3 — Comprendre pourquoi : les utilisateurs */}
      <rect className="i-card" x="38" y="176" width="176" height="96" rx="14" />
      <rect className="i-tint hue-orange" x="56" y="200" width="48" height="48" rx="12" />
      <circle className="i-stroke hue-orange" cx="73" cy="217" r="5.5" />
      <path className="i-stroke hue-orange" d="M63 236c1.5-6 5.5-9 10-9s8.5 3 10 9" />
      <circle className="i-stroke hue-orange" cx="89" cy="219" r="4.5" />
      <path className="i-stroke hue-orange" d="M87 227.5c4 0 7.5 2.5 8.5 7.5" />
      <rect className="i-tint hue-pink" x="118" y="208" width="58" height="13" rx="6.5" />
      <rect className="i-tint hue-green" x="118" y="228" width="40" height="13" rx="6.5" />
      <Num x={38} y={176} n={3} />

      {/* 4 — Décider : l'ampoule */}
      <rect className="i-card" x="262" y="254" width="158" height="80" rx="14" />
      <rect className="i-tint hue-amber" x="278" y="270" width="48" height="48" rx="12" />
      <g className="i-stroke hue-amber" transform="translate(290 282)">
        <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
        <path d="M9 18h6" />
        <path d="M10 22h4" />
      </g>
      <path className="i-line" d="M340 284h60" />
      <path className="i-line" d="M340 298h42" />
      <Num x={262} y={254} n={4} />

      {/* 5 — Prouver l'effet : la jauge, et la courbe qui monte */}
      <rect className="i-card i-card-accent" x="22" y="340" width="240" height="76" rx="14" />
      <path className="i-stroke hue-green" d="M44 396a24 24 0 0 1 48 0" />
      <path className="i-stroke hue-green" d="M68 396l12-14" />
      <circle className="i-fill hue-green" cx="68" cy="396" r="3" />
      <path className="i-stroke hue-green" d="M112 398l26-14 22 6 30-22 24-8" />
      <circle className="i-tint hue-green" cx="214" cy="360" r="4" />
      <path className="i-line" d="M112 360h44" />
      <Num x={22} y={340} n={5} />

      {/* — Quelques étincelles — */}
      <path className="i-stroke hue-amber" d="M410 30v12M404 36h12" />
      <path className="i-stroke hue-cyan" d="M226 22v8M222 26h8" />
      <circle className="i-stroke hue-pink" cx="384" cy="392" r="5" />
      <circle className="i-stroke hue-green" cx="236" cy="150" r="3.5" />
    </svg>
  );
}
